import { FIREWALL_ZONES } from '@connaro/shared';
import { useState } from 'react';
import { ApiError, api } from '../api/client';
import { useAsync } from '../hooks/useAsync';
import { useT } from '../i18n/I18nProvider';
import { Banner, EmptyState, ErrorState, Loading } from './states';

interface ZoneMember {
  id: string;
  zone: string;
  cidr: string;
}

/**
 * Zone membership for one edge: which prefixes belong to which zone.
 *
 * Zones are how a rule is written once and stays correct as networks change — a
 * rule to the LAN zone follows the LAN's prefixes without being rewritten. This is
 * where those prefixes are assigned; the firewall panel above writes rules against
 * the zones defined here.
 */
export function FirewallZonesPanel({ deviceId }: { deviceId: string }): JSX.Element {
  const t = useT();
  const { data, error, loading, reload } = useAsync(
    () => api.get<ZoneMember[]>(`/firewall/devices/${deviceId}/zones`),
    [deviceId],
  );
  const [zone, setZone] = useState<string>(FIREWALL_ZONES[0]);
  const [cidr, setCidr] = useState('');
  const [submitError, setSubmitError] = useState<string | null>(null);

  const add = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    setSubmitError(null);
    try {
      await api.post(`/firewall/devices/${deviceId}/zones`, { zone, cidr });
      setCidr('');
      reload();
    } catch (err) {
      setSubmitError(err instanceof ApiError ? err.message : t('errors.unexpected'));
    }
  };

  const remove = async (id: string): Promise<void> => {
    await api.del(`/firewall/zones/${id}`);
    reload();
  };

  return (
    <div className="card">
      <h2>{t('firewall.zones')}</h2>

      <form className="card form" onSubmit={(e) => void add(e)}>
        {submitError ? <Banner variant="error">{submitError}</Banner> : null}
        <label className="field">
          <span>{t('firewall.zone')}</span>
          <select value={zone} onChange={(e) => setZone(e.target.value)}>
            {FIREWALL_ZONES.map((z) => (
              <option key={z} value={z}>
                {z}
              </option>
            ))}
          </select>
        </label>
        <label className="field">
          <span>{t('firewall.prefix')}</span>
          <input
            placeholder="10.20.0.0/24"
            value={cidr}
            onChange={(e) => setCidr(e.target.value)}
          />
        </label>
        <button type="submit" className="btn">
          {t('firewall.addToZone')}
        </button>
      </form>

      {loading ? (
        <Loading />
      ) : error ? (
        <ErrorState message={error.message} onRetry={reload} />
      ) : !data || data.length === 0 ? (
        <EmptyState hint={t('firewall.noZones')} />
      ) : (
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>{t('firewall.zone')}</th>
                <th>{t('firewall.prefix')}</th>
                <th aria-label="actions" />
              </tr>
            </thead>
            <tbody>
              {data.map((member) => (
                <tr key={member.id}>
                  <td>{member.zone}</td>
                  <td>
                    <code>{member.cidr}</code>
                  </td>
                  <td className="row-actions">
                    <button
                      type="button"
                      className="btn btn--ghost btn--danger"
                      onClick={() => void remove(member.id)}
                    >
                      {t('firewall.delete')}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
